import { useState } from "react";
import { X, Download, Globe } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// HubSpot Configuration for Guide Downloads
const HUBSPOT_PORTAL_ID = "244712680";
const HUBSPOT_GUIDE_FORM_GUID = "226ed23a-33fb-494c-ad25-4a344d6b9a7f";

// PDF URLs
const GUIDE_URLS = {
  english: "https://customer-assets.emergentagent.com/job_b0be9df2-3d3f-47b3-ad10-6eae685c0f13/artifacts/xlldgpj6_FullGuideEnglish.pdf",
  spanish: "https://customer-assets.emergentagent.com/job_b0be9df2-3d3f-47b3-ad10-6eae685c0f13/artifacts/xfj3suli_FullGuideSpanish.pdf"
};

export const GuideModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDownloads, setShowDownloads] = useState(false);
  const [isReturningUser, setIsReturningUser] = useState(false);

  // Check if user has already downloaded on mount
  useState(() => {
    const hasDownloaded = localStorage.getItem('guideDownloaded');
    if (hasDownloaded) {
      setIsReturningUser(true);
      setShowDownloads(true);
    }
  }, []);

  // Also check when modal opens
  const checkReturningUser = () => {
    const hasDownloaded = localStorage.getItem('guideDownloaded');
    if (hasDownloaded) {
      setIsReturningUser(true);
      setShowDownloads(true);
    }
  };

  // Run check when modal opens
  if (isOpen && !showDownloads) {
    const hasDownloaded = localStorage.getItem('guideDownloaded');
    if (hasDownloaded && !isReturningUser) {
      setIsReturningUser(true);
      setShowDownloads(true);
    }
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const submitToHubSpot = async (data) => {
    const hubspotEndpoint = `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_GUIDE_FORM_GUID}`;
    
    const hubspotData = {
      fields: [
        { name: "firstname", value: data.name },
        { name: "email", value: data.email }
      ],
      context: {
        pageUri: window.location.href,
        pageName: "Next Chapter Moves NC - Guide Download"
      }
    };

    const response = await fetch(hubspotEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(hubspotData)
    });

    if (!response.ok) {
      console.error("HubSpot submission failed");
    }

    return response;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save to both database and HubSpot in parallel
      await Promise.all([
        axios.post(`${API}/guide-download`, formData),
        submitToHubSpot(formData)
      ]);
      
      // Save to localStorage so we remember this user
      localStorage.setItem('guideDownloaded', 'true');
      localStorage.setItem('guideUserName', formData.name);
      
      toast.success("Thank you!", {
        description: "Your download links are ready below."
      });
      setShowDownloads(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong", {
        description: "Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ name: "", email: "" });
    // Don't reset showDownloads if returning user
    if (!isReturningUser) {
      setShowDownloads(false);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-[#0F172A]/70 backdrop-blur-sm z-[100]"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div 
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90%] max-w-md bg-white p-8 shadow-2xl z-[101]"
        data-testid="guide-modal"
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-[#94A3B8] hover:text-[#0F172A] transition-colors"
          aria-label="Close modal"
        >
          <X size={24} />
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[#D4AF37] flex items-center justify-center mx-auto mb-4">
            <Download className="text-[#0F172A]" size={32} />
          </div>
          <h3 className="text-2xl font-bold text-[#0F172A] font-serif">
            {isReturningUser ? "Welcome Back!" : (showDownloads ? "Your Guide is Ready!" : "Get Your Free Guide")}
          </h3>
          <p className="text-[#475569] mt-2">
            {isReturningUser 
              ? "Your guides are ready to download again."
              : (showDownloads 
                ? "Click below to download in your preferred language."
                : "Enter your info to access the Next Chapter Guide."
              )
            }
          </p>
        </div>

        {!showDownloads ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="modal-name" className="block text-sm font-medium text-[#0F172A] mb-1">
                Name *
              </label>
              <input
                type="text"
                id="modal-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="Your name"
                data-testid="guide-modal-name"
              />
            </div>

            <div>
              <label htmlFor="modal-email" className="block text-sm font-medium text-[#0F172A] mb-1">
                Email *
              </label>
              <input
                type="email"
                id="modal-email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="your@email.com"
                data-testid="guide-modal-email"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn-primary w-full justify-center mt-6"
              data-testid="guide-modal-submit"
            >
              {isSubmitting ? (
                <>
                  <span className="spinner" />
                  Processing...
                </>
              ) : (
                <>
                  <Download size={18} />
                  Get My Free Guide
                </>
              )}
            </button>

            <p className="text-xs text-[#94A3B8] text-center">
              We respect your privacy. No spam, ever.
            </p>
          </form>
        ) : (
          <div className="space-y-4">
            <a
              href={GUIDE_URLS.english}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between w-full p-4 bg-[#F8FAFC] border-2 border-[#0F172A] hover:bg-[#0F172A] hover:text-white transition-colors group"
              data-testid="download-english"
            >
              <div className="flex items-center gap-3">
                <Globe size={20} />
                <span className="font-semibold">English Version</span>
              </div>
              <Download size={20} className="text-[#D4AF37]" />
            </a>

            <a
              href={GUIDE_URLS.spanish}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between w-full p-4 bg-[#F8FAFC] border-2 border-[#0F172A] hover:bg-[#0F172A] hover:text-white transition-colors group"
              data-testid="download-spanish"
            >
              <div className="flex items-center gap-3">
                <Globe size={20} />
                <span className="font-semibold">Versión en Español</span>
              </div>
              <Download size={20} className="text-[#D4AF37]" />
            </a>

            <button
              onClick={handleClose}
              className="btn-outline w-full justify-center mt-4"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </>
  );
};
